/**
 * Burnaston "Capacity to Fill" — reference data + calculation.
 *
 * INTERIM: Card 1 is computed on the frontend from this constant, not persisted server-side
 * (a deliberate exception to "calc in the backend" for this one card — chosen for speed and
 * easy extensibility: add a vehicle = one line here). Values are product-supplied via the
 * ticket. TODO: revisit if these should live server-side / be verified with Toyota.
 * See docs/current/burnaston-cards.
 */

export interface VehicleType {
  name: string
  /** Rated H2 capacity in kg at max pressure. */
  capacityKg: number
  /** Rated max working pressure in bar. */
  maxPressureBar: number
}

/** Refueller compressor max output pressure (bar) — from the ticket. */
export const REFUELLER_MAX_PRESSURE_BAR = 350

export const VEHICLE_TYPES: VehicleType[] = [
  { name: "Hilux", capacityKg: 6.8, maxPressureBar: 700 },
  { name: "Mirai", capacityKg: 5.4, maxPressureBar: 700 },
  { name: "Caetano", capacityKg: 37, maxPressureBar: 350 },
]

export interface VehicleFill {
  vehicle: string
  count: number
  /**
   * How full a single fill gets this vehicle, 0–1.
   *
   * Below 1 the refueller's output pressure is the binding constraint, not the tank: at Burnaston's
   * 350 bar a 700 bar Hilux only ever takes half its 6.8 kg, which is why the counts look generous
   * next to the vehicle's nameplate capacity.
   */
  fillFraction: number
  /**
   * The figures this row was computed from. Carried so `fillExplanation` quotes the values actually
   * used rather than re-deriving them and being free to drift from the number beside it.
   */
  capacityKg: number
  maxPressureBar: number
  refuellerMaxPressureBar: number
}

/**
 * Per vehicle type, how many could be filled from the current stored hydrogen:
 *   fill fraction       = min(1, refueller pressure ÷ vehicle pressure)
 *   refuellable capacity = capacity × fill fraction
 *   count               = floor( current weight ÷ refuellable capacity )
 * Returns [] for a missing/negative weight.
 *
 * The fraction is clamped at 1 because a refueller that out-pressures the vehicle still only fills
 * its tank — unclamped, a 700 bar refueller would treat a 350 bar Caetano as taking 74 kg and halve
 * its count. No effect at Burnaston's 350 bar, but it stops the card reading "fills to 200%".
 */
export function computeVehiclesToFill(
  currentWeightKg: number | null | undefined,
  vehicleTypes: VehicleType[] = VEHICLE_TYPES,
  refuellerMaxPressureBar: number = REFUELLER_MAX_PRESSURE_BAR,
): VehicleFill[] {
  if (currentWeightKg == null || !Number.isFinite(currentWeightKg) || currentWeightKg < 0) {
    return []
  }
  return vehicleTypes.map((v) => {
    const fillFraction =
      v.maxPressureBar > 0 ? Math.min(1, refuellerMaxPressureBar / v.maxPressureBar) : 0
    const refuellableCapacity = v.capacityKg * fillFraction
    const count = refuellableCapacity > 0 ? Math.floor(currentWeightKg / refuellableCapacity) : 0
    return {
      vehicle: v.name,
      count,
      fillFraction,
      capacityKg: v.capacityKg,
      maxPressureBar: v.maxPressureBar,
      refuellerMaxPressureBar,
    }
  })
}

/**
 * The supporting line beside the count — `Can fill to` / `50%` / `tank capacity`.
 *
 * Split into three because the card breaks the line straight after the percentage and gives it a
 * fixed width, so that segment has to be addressable on its own. Split here rather than in the
 * component so the copy stays in one place.
 *
 * Always rendered, not only when clipped: a line that appeared sometimes would leave the reader
 * unsure whether a missing one meant full or meant unknown.
 */
export function fillPercentParts(fillFraction: number): {
  lead: string
  percent: string
  trail: string
} {
  return {
    lead: "",
    percent: `${Math.round(fillFraction * 100)}%`,
    trail: "capacity",
  }
}

/** Trims float noise without forcing decimals on a whole number: 3.4 kg, 37 kg. */
function formatKg(kg: number): string {
  return `${Number(kg.toFixed(2))} kg`
}

/**
 * Panel copy for the fill line, naming the two pressures that decide it.
 *
 * Two sentences in a fixed order: the system's, word-for-word identical on every vehicle, then the
 * vehicle's, keeping its shape whichever is on screen. The card is paged, so reading the three in
 * succession is a comparison, and that is only easy if the sentences differ solely in what actually
 * differs. Both variants close on a kg figure for the same reason.
 *
 * The refueller pressure leads because it is the one term an operator cannot read off the vehicle.
 */
export function fillExplanation(fill: VehicleFill): string {
  const system = `This system has a max refuelling pressure of ${fill.refuellerMaxPressureBar} bar.`
  const rated = formatKg(fill.capacityKg)
  const tank = `The ${fill.vehicle}'s tank is rated to ${fill.maxPressureBar} bar`

  if (fill.fillFraction >= 1) {
    return `${system} ${tank}, so each fill is its full ${rated} capacity.`
  }
  return (
    `${system} ${tank}, so each fill reaches ${formatKg(fill.capacityKg * fill.fillFraction)} — ` +
    `${Math.round(fill.fillFraction * 100)}% of its ${rated} capacity.`
  )
}
